<template>
  <div class="profile-account">
    <div v-if="!user.email_verified_at" class="verify-email-section mb-3">
      <b-card>
        <h3>
          {{ $t("views.profile.account.verify_email.title") }}
          <b-badge pill variant="danger">!</b-badge>
        </h3>

        <p>
          {{ $t("views.profile.account.verify_email.content") }}
        </p>
        <p>
          {{ $t("views.profile.account.verify_email.call_to_action") }}
        </p>

        <b-button
          :disabled="verificationLoading"
          variant="primary"
          @click="resendEmailVerification"
          >{{ $t("views.profile.account.verify_email.button") }}</b-button
        >
      </b-card>
    </div>

    <div class="account-columns">
      <b-card class="account-column">
        <h3>{{ $t("views.profile.account.password.title") }}</h3>

        <user-password-form
          ref="passwordForm"
          :user="item"
          :required="true"
          :must-validate-current="true"
          @updated="passwordUpdated"
        />
      </b-card>
      <b-card class="account-column">
        <h3>{{ $t("views.profile.account.email.title") }}</h3>

        <p>{{ $t("views.profile.account.email.current_email") }}&nbsp;: {{ item.email }}.</p>

        <user-email-form ref="emailForm" :user="item" @updated="updateEmail" />
      </b-card>
    </div>

    <div class="form__section mt-3">
      <a id="deactivate" href="#deactivate"></a>
      <h2>{{ $t("views.profile.account.title") }}</h2>

      <p>
        <i18n v-if="!(item.balance > 0)" path="views.profile.account.delete_warning">
          <template #loans_url>
            <b-link :to="`/profile/loans?status=in_process`">
              {{ $t("views.profile.account.delete_warning_loans_url") }}
            </b-link>
          </template>
        </i18n>
        <i18n v-else path="views.profile.account.delete_warning_balance">
          <template #loans_url>
            <b-link :to="`/profile/loans?status=in_process`">
              {{ $t("views.profile.account.delete_warning_loans_url") }}
            </b-link>
          </template>
          <template #balance_url>
            <b-link v-b-modal.archive-account-claim-modal>
              {{ $t("views.profile.account.delete_warning_balance_url") }}
            </b-link>
          </template>
        </i18n>
      </p>

      <b-modal
        v-if="item.balance > 0"
        id="archive-account-claim-modal"
        :title="$t('views.profile.account.claim_credits.title')"
        size="lg"
        hide-footer
      >
        <user-claim-credits-box
          :user="$store.state.user"
          @claimed="() => $bvModal.hide('archive-account-claim-modal')"
          @cancel="() => $bvModal.hide('archive-account-claim-modal')"
        />
      </b-modal>

      <b-card class="mb-3 border-danger border">
        <p>
          {{ $t("views.profile.account.deactivate.description") }}
        </p>

        <validated-button
          variant="outline-danger"
          icon="person-x"
          :action="deactivateAccount"
          :label="$t('views.profile.account.deactivate.title')"
          :question="$t('views.profile.account.deactivate.confirmation')"
        />
      </b-card>

      <b-card class="border-danger border">
        <p>
          {{ $t("views.profile.account.delete.description") }}
        </p>

        <validated-button
          variant="outline-danger"
          icon="x-octagon-fill"
          :action="deleteAccountAndData"
          :label="$t('views.profile.account.delete.title')"
          :question="$t('views.profile.account.delete.confirmation')"
        />
      </b-card>
    </div>
  </div>
</template>

<script>
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import UserClaimCreditsBox from "@/components/User/ClaimCreditsBox.vue";
import UserEmailForm from "@/components/User/EmailForm.vue";
import UserPasswordForm from "@/components/User/PasswordForm.vue";
import { del } from "@/requests/server";

export default {
  name: "ProfileAccount",
  components: {
    ValidatedButton,
    UserClaimCreditsBox,
    UserEmailForm,
    UserPasswordForm,
  },
  data() {
    return {
      item: {
        ...this.$store.state.user,
      },
      verificationLoading: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
  },
  methods: {
    async deactivateAccount() {
      await del(`/users/${this.item.id}`, {
        notifications: {
          action: this.$t("views.profile.account.deactivate.action"),
          onSuccess: this.$t("views.profile.account.deactivate.success"),
        },
      });

      await this.$router.push("/login?logout=1");
      await this.$store.dispatch("logout");
    },
    async deleteAccountAndData() {
      await del(`/users/${this.item.id}`, {
        axiosRequestConfig: { data: { delete_data: true } },
        notifications: {
          action: this.$t("views.profile.account.delete.action"),
          onSuccess: this.$t("views.profile.account.delete.success"),
        },
      });

      await this.$router.push("/login?logout=1");
      await this.$store.dispatch("logout");
    },
    passwordUpdated() {
      this.$store.commit("addNotification", {
        title: this.$t("views.profile.account.password_change_modal.title"),
        variant: "success",
        content: this.$t("views.profile.account.password_change_modal.content"),
      });
    },
    async resendEmailVerification() {
      try {
        this.verificationLoading = true;
        await this.$store.dispatch("account/resendEmailVerification");
        this.$store.commit("addNotification", {
          content: this.$t("views.profile.account.resend_email_verification.success_content"),
          title: this.$t("views.profile.account.resend_email_verification.title"),
          variant: "success",
        });
      } catch {
        this.$store.commit("addNotification", {
          content: this.$t("views.profile.account.resend_email_verification.error_content"),
          title: this.$t("views.profile.account.resend_email_verification.title"),
          variant: "danger",
        });
      } finally {
        this.verificationLoading = false;
      }
    },
    updateEmail({ email }) {
      this.$store.commit("user", { ...this.$store.state.user, email });
    },
  },
};
</script>

<style lang="scss">
.profile-account {
  margin-bottom: 3em;
  container-type: inline-size;

  .verify-email-section {
    .badge-pill {
      position: relative;
      bottom: 3px;
    }
  }

  .account-columns {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;

    @container (width > 40rem) {
      flex-direction: row;
    }

    .account-column {
      flex-basis: 50%;
      flex-grow: 1;
    }
  }
}
</style>
